import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { AppValidationReport } from './types'

interface ValidatorStatsCardsProps {
  report: AppValidationReport | null
  running: boolean
}

interface CardSpec {
  testId: string
  label: string
  value: number | undefined
  tone: 'success' | 'destructive' | 'warning' | 'neutral'
}

export function ValidatorStatsCards({ report, running }: ValidatorStatsCardsProps) {
  if (running && !report) {
    return (
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="p-4" data-testid="stat-card-skeleton">
            <div className="h-3 w-20 rounded bg-muted animate-pulse mb-2" />
            <div className="h-7 w-12 rounded bg-muted animate-pulse" />
          </Card>
        ))}
      </div>
    )
  }

  const specs: CardSpec[] = [
    {
      testId: 'stat-card-pass',
      label: 'Checks passed',
      value: report?.pass,
      tone: 'success',
    },
    {
      testId: 'stat-card-violations',
      label: 'Violations',
      value: report?.violations,
      tone: report && report.violations > 0 ? 'destructive' : 'neutral',
    },
    {
      testId: 'stat-card-warnings',
      label: 'Warnings',
      value: report?.warnings,
      tone: report && report.warnings > 0 ? 'warning' : 'neutral',
    },
  ]

  const toneClass = {
    success: 'text-success',
    destructive: 'text-destructive',
    warning: 'text-warning',
    neutral: 'text-foreground',
  }

  return (
    <div className="grid grid-cols-3 gap-2.5 mb-4">
      {specs.map((s) => (
        <Card
          key={s.testId}
          data-testid={s.testId}
          className={cn('p-4', toneClass[s.tone])}
        >
          <div className="text-xs text-muted-foreground">{s.label}</div>
          <div className={cn('text-3xl font-bold mt-1', toneClass[s.tone])}>
            {s.value === undefined ? '—' : s.value}
          </div>
        </Card>
      ))}
    </div>
  )
}
